<template>
  <box-label-border :label="$t('components.battle_report.label')">
    <div class="w-full h-full overflow-y-auto">
      <!-- Header -->
      <section class="text-left mb-4">
        <span
          class="font-bold uppercase"
          v-text="$t('components.battle_report.title')"
        ></span
        ><br />
        <span
          v-text="
            $t('components.battle_report.planet', {
              coordinates: report.defender.planetCoordinates,
              name: report.defender.planetName
            })
          "
        ></span
        ><br />
        <span
          class="font-bold"
          v-text="$t('components.battle_report.datetime.label')"
        ></span
        >&nbsp;<span
          v-text="
            $t('components.battle_report.datetime.value', {
              date: formatDate(report.date),
              time: formatTime(report.date)
            })
          "
        ></span>
      </section>
      <!-- Attackers/Defenders -->
      <table>
        <tr>
          <td style="padding-right: 2rem;padding-bottom: 2rem;">
            <span
              class="font-bold"
              v-text="$t('components.battle_report.attacker')"
            ></span
            >&nbsp;<span v-text="report.attacker.name"></span>
          </td>
          <td>
            <span
              class="font-bold"
              v-text="$t('components.battle_report.defender')"
            ></span
            >&nbsp;<span v-text="report.defender.name"></span>
          </td>
        </tr>
        <tr>
          <td style="padding-right: 2rem;padding-bottom: 2rem;">
            <span
              class="font-bold uppercase"
              v-text="$t('components.battle_report.pre_battle.attacking_fleet')"
            ></span>
            <table v-if="report.attackerShips.length > 0">
              <tr :key="item.type" v-for="item of report.attackerShips">
                <td v-text="shipTypes[item.type].name"></td>
                <td class="text-right" v-text="formatNumber(item.amount)"></td>
              </tr>
            </table>

            <span
              v-else
              class="block italic"
              v-text="$t('components.battle_report.no_entries')"
            ></span>
          </td>
          <td style="padding-bottom: 2rem;">
            <span
              class="font-bold uppercase"
              v-text="$t('components.battle_report.pre_battle.defending_fleet')"
            ></span>
            <table v-if="report.defenderShips.length > 0">
              <tr :key="item.type" v-for="item of report.defenderShips">
                <td v-text="shipTypes[item.type].name"></td>
                <td class="text-right" v-text="formatNumber(item.amount)"></td>
              </tr>
            </table>

            <span
              v-else
              v-text="$t('components.battle_report.no_entries')"
              class="block italic"
            ></span>
          </td>
        </tr>
        <tr>
          <td style="padding-right: 2rem;padding-bottom: 2rem;"></td>
          <td style="padding-bottom: 2rem;">
            <span
              class="font-bold uppercase"
              v-text="
                $t('components.battle_report.pre_battle.planetary_defense')
              "
            ></span>
            <table v-if="report.defenderDefenses.length > 0">
              <tr :key="item.type" v-for="item of report.defenderDefenses">
                <td v-text="defenseTypes[item.type].name"></td>
                <td class="text-right" v-text="formatNumber(item.amount)"></td>
              </tr>
            </table>

            <span
              v-else
              v-text="$t('components.battle_report.no_entries')"
              class="block italic"
            ></span>
          </td>
        </tr>
        <tr>
          <td style="padding-right: 2rem;padding-bottom: 2rem;">
            <span
              class="font-bold uppercase"
              v-text="$t('components.battle_report.pre_battle.attacker_stats')"
            ></span>
            <table>
              <tr>
                <td
                  v-text="
                    $t('components.battle_report.pre_battle.shields', {
                      amount: formatBuff(report.attacker.totalShieldBuff)
                    })
                  "
                ></td>
                <td
                  class="text-right"
                  v-text="formatNumber(report.attacker.totalShield)"
                ></td>
              </tr>
              <tr>
                <td
                  v-text="
                    $t('components.battle_report.pre_battle.structure', {
                      amount: formatBuff(report.attacker.totalStructureBuff)
                    })
                  "
                ></td>
                <td
                  class="text-right"
                  v-text="formatNumber(report.attacker.totalStructure)"
                ></td>
              </tr>
              <tr>
                <td
                  v-text="
                    $t('components.battle_report.pre_battle.weapons', {
                      amount: formatBuff(report.attacker.totalWeaponsBuff)
                    })
                  "
                ></td>
                <td
                  class="text-right"
                  v-text="formatNumber(report.attacker.totalWeapons)"
                ></td>
              </tr>
              <tr v-if="report.attacker.totalHeal > 0">
                <td
                  v-text="
                    $t('components.battle_report.pre_battle.heal', {
                      amount: formatBuff(report.attacker.totalHealBuff)
                    })
                  "
                ></td>
                <td
                  class="text-right"
                  v-text="formatNumber(report.attacker.totalHeal)"
                ></td>
              </tr>
              <tr>
                <td
                  v-text="$t('components.battle_report.pre_battle.units')"
                ></td>
                <td
                  class="text-right"
                  v-text="formatNumber(report.attacker.totalUnits)"
                ></td>
              </tr>
            </table>
          </td>
          <td style="padding-bottom: 2rem;">
            <span
              class="font-bold uppercase"
              v-text="$t('components.battle_report.pre_battle.defender_stats')"
            ></span>
            <table>
              <tr>
                <td
                  v-text="
                    $t('components.battle_report.pre_battle.shields', {
                      amount: formatBuff(report.defender.totalShieldBuff)
                    })
                  "
                ></td>
                <td
                  class="text-right"
                  v-text="formatNumber(report.defender.totalShield)"
                ></td>
              </tr>
              <tr>
                <td
                  v-text="
                    $t('components.battle_report.pre_battle.structure', {
                      amount: formatBuff(report.defender.totalStructureBuff)
                    })
                  "
                ></td>
                <td
                  class="text-right"
                  v-text="formatNumber(report.defender.totalStructure)"
                ></td>
              </tr>
              <tr>
                <td
                  v-text="
                    $t('components.battle_report.pre_battle.weapons', {
                      amount: formatBuff(report.defender.totalWeaponsBuff)
                    })
                  "
                ></td>
                <td
                  class="text-right"
                  v-text="formatNumber(report.defender.totalWeapons)"
                ></td>
              </tr>
              <tr v-if="report.defender.totalHeal > 0">
                <td
                  v-text="
                    $t('components.battle_report.pre_battle.heal', {
                      amount: formatBuff(report.defender.totalHealBuff)
                    })
                  "
                ></td>
                <td
                  class="text-right"
                  v-text="formatNumber(report.defender.totalHeal)"
                ></td>
              </tr>
              <tr>
                <td
                  v-text="$t('components.battle_report.pre_battle.units')"
                ></td>
                <td
                  class="text-right"
                  v-text="formatNumber(report.defender.totalUnits)"
                ></td>
              </tr>
            </table>
          </td>
        </tr>
      </table>

      <!-- Rounds -->
      <section class="text-left mb-8">
        <section
          :class="{ 'mb-8': round !== report.log.length - 1 }"
          :key="round"
          v-for="(entries, round) of report.log"
        >
          <p :key="line" v-for="(text, line) of entries" v-text="text"></p>
        </section>
        <p
          v-text="
            $t('components.battle_report.battle.duration', {
              rounds: $tc(
                'components.battle_report.battle.round',
                report.rounds
              )
            })
          "
        ></p>
        <p
          v-if="report.winner === 0"
          v-text="$t('components.battle_report.battle.stalemate_destroyed')"
        ></p>
        <p
          v-else-if="report.winner === 1"
          v-text="$t('components.battle_report.battle.attacker_wins')"
        ></p>
        <p
          v-else-if="report.winner === 2"
          v-text="$t('components.battle_report.battle.defender_wins')"
        ></p>
        <p
          v-else-if="report.winner === 3"
          v-text="$t('components.battle_report.battle.stalemate_survived')"
        ></p>
      </section>
      <!-- Bounty / Debris -->
      <table>
        <tr>
          <td style="padding-right: 2rem;padding-bottom: 2rem;">
            <span
              class="font-bold uppercase"
              v-text="$t('components.battle_report.bounty')"
            ></span>
            <table v-if="report.bounty.length > 0">
              <tr :key="entry.type" v-for="entry of report.bounty">
                <td v-text="resourceTypes[entry.type].name"></td>
                <td class="text-right" v-text="formatNumber(entry.amount)"></td>
              </tr>
            </table>
          </td>
          <td style="padding-bottom: 2rem;">
            <span
              class="font-bold uppercase"
              v-text="$t('components.battle_report.debris')"
            ></span>
            <table>
              <tr :key="entry.type" v-for="entry of report.debris">
                <td v-text="resourceTypes[entry.type].name"></td>
                <td class="text-right" v-text="formatNumber(entry.amount)"></td>
              </tr>
            </table>
          </td>
        </tr>
      </table>

      <!-- Result -->
      <p
        class="text-left"
        v-text="$t('components.battle_report.post_battle.label')"
      ></p>
      <table>
        <tr>
          <td style="padding-right: 2rem;padding-bottom: 2rem;">
            <span
              class="font-bold uppercase"
              v-text="
                $t('components.battle_report.post_battle.attacking_fleet')
              "
            ></span>
            <table v-if="report.remainingAttackerShips.length > 0">
              <tr
                :key="item.type"
                v-for="item of report.remainingAttackerShips"
              >
                <td v-text="shipTypes[item.type].name"></td>
                <td
                  class="text-right"
                  v-text="formatNumber(item.amount).toString()"
                ></td>
              </tr>
            </table>

            <span
              v-else
              class="block italic"
              v-text="$t('components.battle_report.no_entries')"
            ></span>

            <span
              class="block"
              v-if="report.attackerExperience > 0"
              v-text="
                $t('components.battle_report.post_battle.attacker_experience', {
                  value: formatNumber(report.attackerExperience)
                })
              "
            ></span>
          </td>
          <td style="padding-bottom: 2rem;">
            <span
              class="font-bold uppercase"
              v-text="
                $t('components.battle_report.post_battle.defending_fleet')
              "
            ></span>
            <template v-if="report.remainingDefenderShips.length > 0">
              <table>
                <tr
                  :key="item.type"
                  v-for="item of report.remainingDefenderShips"
                >
                  <td v-text="shipTypes[item.type].name"></td>
                  <td
                    class="text-right"
                    v-text="formatNumber(item.amount)"
                  ></td>
                  <td
                    class="text-left"
                    v-text="
                      item.restored > 0
                        ? ' (+' + formatNumber(item.restored) + ')'
                        : ''
                    "
                  ></td>
                </tr>
              </table>

              <span
                class="block mt-4 mb-4"
                v-text="
                  $t(
                    'components.battle_report.post_battle.infrastructure_restored',
                    {
                      value:
                        Math.round(report.civilianRestoreFactor * 100) + '%'
                    }
                  )
                "
              ></span>
            </template>
            <span
              v-else
              v-text="$t('components.battle_report.no_entries')"
              class="block italic"
            ></span>
          </td>
        </tr>
        <tr>
          <td></td>
          <td style="padding-bottom: 2rem;">
            <span
              class="font-bold uppercase"
              v-text="
                $t('components.battle_report.post_battle.planetary_defense')
              "
            ></span>
            <template v-if="report.remainingDefenderDefenses.length > 0">
              <table>
                <tr
                  :key="item.type"
                  v-for="item of report.remainingDefenderDefenses"
                >
                  <td v-text="defenseTypes[item.type].name"></td>
                  <td
                    class="text-right"
                    v-text="formatNumber(item.amount)"
                  ></td>
                  <td
                    class="text-left"
                    v-text="
                      item.restored > 0
                        ? ' (+' + formatNumber(item.restored) + ')'
                        : ''
                    "
                  ></td>
                </tr>
              </table>

              <span
                class="block mt-4 mb-4"
                v-text="
                  $t('components.battle_report.post_battle.defense_restored', {
                    value: Math.round(report.defenseRestoreFactor * 100) + '%'
                  })
                "
              ></span>
            </template>

            <span
              v-else
              v-text="$t('components.battle_report.no_entries')"
              class="block italic"
            ></span>

            <span
              class="block"
              v-if="report.defenderExperience > 0"
              v-text="
                $t('components.battle_report.post_battle.attacker_experience', {
                  value: formatNumber(report.defenderExperience)
                })
              "
            ></span>
          </td>
        </tr>
      </table>
    </div>
  </box-label-border>
</template>

<script>
import { mapGetters } from "vuex";
import BoxLabelBorder from "@/components/boxes/BoxLabelBorder";

import { StringHelper } from "@/helpers/string-helper";
import { DateHelper } from "@/helpers/date-helper";
import BattleReport from "@/store/model/battle-report";

export default {
  name: "BattleReport",
  components: { BoxLabelBorder },
  props: {
    report: {
      type: BattleReport,
      required: true
    }
  },
  computed: {
    ...mapGetters("data", [
      "shipTypes",
      "defenseTypes",
      "researchTypes",
      "resourceTypes"
    ])
  },
  methods: {
    formatNumber(value) {
      return value >= 0 ? StringHelper.formatNumber(value) : "-";
    },
    formatDate(date) {
      return DateHelper.formatDate(date);
    },
    formatTime(date) {
      return DateHelper.formatTime(date);
    },
    formatBuff(buff) {
      return Math.round(buff * 100) + "%";
    }
  }
};
</script>

<style scoped>
p {
  margin-bottom: 1rem;
}
td {
  vertical-align: top;
}
td:not(.text-right) {
  text-align: left;
}
td:not(:last-child) {
  padding-right: 1rem;
}
</style>
